import React, { useState } from 'react';
import { Outlet, useParams } from 'react-router-dom';
import { StoreProvider, useStoreContext } from '../../context/StoreContext';
import { StoreHeader, StoreFooter, CartSheet } from '../store';
import { useCart } from '../../context/CartContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { storePath, getSubdomain } from '../../utils/storeUtils';

import StoreBlocked from '../store/StoreBlocked';

const StoreLayoutContent = () => {
    const { store, theme, storeAppearance, platformInfo, loading, subdomain, collections, error } = useStoreContext();
    const { cartCount, setCartOpen } = useCart();
    const [searchTerm, setSearchTerm] = useState("");
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const navigate = useNavigate();

    // Dynamic Font Loading
    React.useEffect(() => {
        if (!theme?.font_family) return;

        // Extract font name (e.g., "Montserrat, sans-serif" -> "Montserrat")
        const fontName = theme.font_family.split(',')[0].trim().replace(/['"]/g, '');
        
        // System fonts don't need loading
        const systemFonts = ['Arial', 'Helvetica', 'Times New Roman', 'Courier New', 'Georgia', 'Verdana', 'Inter'];
        if (systemFonts.includes(fontName)) return;

        const fontId = `google-font-${fontName.toLowerCase().replace(/\s+/g, '-')}`;
        if (!document.getElementById(fontId)) {
            const link = document.createElement('link');
            link.id = fontId;
            link.rel = 'stylesheet';
            link.href = `https://fonts.googleapis.com/css2?family=${fontName.replace(/\s+/g, '+')}:wght@300;400;500;600;700;800;900&display=swap`;
            document.head.appendChild(link);
        }
    }, [theme?.font_family]);

    if (error?.response?.status === 403 || (store && store.is_active === false)) {
        // The API names the reason (suspension vs e-Displayroom switched off) so
        // the page can say the right thing; fall back to suspension when the
        // block came from a stale store object rather than a fresh response.
        return <StoreBlocked theme={theme} reason={error?.response?.data?.code || 'STORE_SUSPENDED'} />;
    }

    if (loading) {
        return (
            <div className="min-h-screen bg-white flex flex-col">
                {/* Minimal Header Skeleton */}
                <div className="h-20 border-b flex items-center justify-between px-6">
                    <div className="w-10 h-10 rounded-lg animate-shimmer" />
                    <div className="flex gap-4">
                        <div className="w-20 h-4 rounded animate-shimmer" />
                        <div className="w-20 h-4 rounded animate-shimmer" />
                    </div>
                </div>
                <div className="flex-1 flex items-center justify-center">
                   <div className="w-10 h-10 border-4 border-slate-200 border-t-slate-900 rounded-full animate-spin"></div>
                </div>
            </div>
        );
    }

    if (!store) {
        return <Outlet />; // Let the page handle Not Found
    }

    return (
        <>
            <style dangerouslySetInnerHTML={{ __html: `
                .storefront-root, 
                .storefront-root * {
                    font-family: ${theme.font_family} !important;
                }
                /* Specialized overrides for headers and inputs that might have strong defaults */
                .storefront-root h1, 
                .storefront-root h2, 
                .storefront-root h3, 
                .storefront-root h4, 
                .storefront-root h5, 
                .storefront-root h6,
                .storefront-root input,
                .storefront-root textarea,
                .storefront-root button {
                    font-family: ${theme.font_family} !important;
                }
            `}} />
            <div className="min-h-screen flex flex-col animate-fade-in storefront-root" style={{ backgroundColor: theme.background_color }}>
                <StoreHeader
                    store={store}
                    theme={theme}
                    storeAppearance={storeAppearance}
                    subdomain={subdomain}
                    searchTerm={searchTerm}
                    setSearchTerm={setSearchTerm}
                    mobileMenuOpen={mobileMenuOpen}
                    setMobileMenuOpen={setMobileMenuOpen}
                    collections={collections}
                />

                <main className="flex-grow">
                    <Outlet />
                </main>

                <StoreFooter store={store} theme={theme} platformInfo={platformInfo} />

                <CartSheet
                    theme={theme}
                    onCheckout={() => {
                        if (cartCount === 0) {
                            toast.error("Your cart is empty");
                            return;
                        }
                        setCartOpen(false);
                        navigate(storePath(subdomain, '/checkout'));
                    }}
                />
            </div>
        </>
    );
};

export const StoreLayout = () => {
    const { subdomain: paramSubdomain } = useParams();
    const subdomain = paramSubdomain || getSubdomain();
    return (
        <StoreProvider subdomain={subdomain}>
            <StoreLayoutContent />
        </StoreProvider>
    );
};

export default StoreLayout;
